import React, { useState, useCallback, useEffect } from "react";
import {
  Input,
  Button,
  ScrollArea,
  Badge,
  Separator,
  Skeleton,
  Calendar,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "src/components/ui";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "src/components/ui/avatar";

import { Label } from "src/components/ui/label";

import { toast } from "sonner";

import {
  Search,
  User,
  Filter,
  X,
  MessageSquare,
  MessageCircle,
  Hash,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
} from "lucide-react";

import { cn } from "src/shared/lib/utils/utils";
import { format, parseISO } from "date-fns";
import { ru } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequests } from "src/shared/api/api";
import { 
  formatLastMessageTime, 
  truncatePreview,
  isImageMessage 
} from "./utils";

const filterSchema = z.object({
  search: z.string().optional(),
  status: z.string().optional(),
  channel_id: z.string().optional(),
  contragent_id: z.string().optional(),
  created_range: z.object({
    from: z.date().optional(),
    to: z.date().optional()
  }).optional(),
  updated_range: z.object({
    from: z.date().optional(),
    to: z.date().optional()
  }).optional(),
  sort_by: z.string().default("last_message_time"),
  sort_order: z.string().default("desc"),
});

export default React.memo(function UserList({
  chats,
  loading,
  searchText,
  setSearchText,
  channels,
  selectedChat,
  onSelectChat,
  formatTime,
  token,
  onApplyFilters,
  currentFilters,
  typingStatus,
  onlineStatus
}) {
  const [localSearch, setLocalSearch] = useState(searchText);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [activeFilters, setActiveFilters] = useState(currentFilters || {});
  const [contragentOptions] = useState([]);
  
  
  const [activeTab, setActiveTab] = useState("chats");
  const [allChannels, setAllChannels] = useState(channels || []);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [isAddChannelOpen, setIsAddChannelOpen] = useState(false);
  const [isEditChannelOpen, setIsEditChannelOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showOAuthView, setShowOAuthView] = useState(false);
  const [channelForm, setChannelForm] = useState({
    name: "",
    client_id: "",
    client_secret: ""
  });
  const [channelsSearchQuery, setChannelsSearchQuery] = useState("");

  const filterForm = useForm({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      sort_by: "last_message_time",
      sort_order: "desc",
      ...currentFilters
    },
  });

  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== searchText) {
        setSearchText(localSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearch, searchText, setSearchText]);

  useEffect(() => {
    setActiveFilters(currentFilters || {});
    filterForm.reset({
      sort_by: "last_message_time",
      sort_order: "desc",
      ...currentFilters
    });
  }, [currentFilters, filterForm]);

  
  const loadChannels = useCallback(async () => {
    setLoadingChannels(true);
    try {
      const response = await apiRequests.chats.getChannels({ 
        token,
        skip: 0,
        limit: 100
      });
      
      if (response.data && Array.isArray(response.data)) {
        const activeChannels = response.data;
        setAllChannels(activeChannels);
      }
    } catch (error) {
      console.error("Ошибка загрузки каналов:", error);
      toast.error("Не удалось загрузить список каналов");
    } finally {
      setLoadingChannels(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === "channels") {
      loadChannels();
    }
  }, [activeTab, loadChannels]);

  useEffect(() => {
    if (!isAddChannelOpen) {
      setShowOAuthView(false);
      setChannelForm({
        name: "",
        client_id: "",
        client_secret: ""
      });
    }
  }, [isAddChannelOpen]);

  const handleFilterClick = () => {
    filterForm.reset({
      sort_by: "last_message_time",
      sort_order: "desc",
      ...activeFilters
    });
    setFilterModalVisible(true);
  };

  const applyFilters = (filters) => {
    const queryParams = {
      ...filters,
      created_from: filters.created_range?.from ? format(filters.created_range.from, 'yyyy-MM-dd\'T\'HH:mm:ss') : undefined,
      created_to: filters.created_range?.to ? format(filters.created_range.to, 'yyyy-MM-dd\'T\'HH:mm:ss') : undefined,
      updated_from: filters.updated_range?.from ? format(filters.updated_range.from, 'yyyy-MM-dd\'T\'HH:mm:ss') : undefined,
      updated_to: filters.updated_range?.to ? format(filters.updated_range.to, 'yyyy-MM-dd\'T\'HH:mm:ss') : undefined,
    };

    
    delete queryParams.created_range;
    delete queryParams.updated_range;

    setActiveFilters(filters);
    if (onApplyFilters) {
      onApplyFilters(queryParams);
    }
    setFilterModalVisible(false);
  };

  const resetFilters = () => {
    
    filterForm.reset({
      sort_by: "last_message_time",
      sort_order: "desc",
      search: "",
      status: "",
      channel_id: "",
      contragent_id: "",
      created_range: undefined,
      updated_range: undefined
    });
    
    setActiveFilters({});
    if (onApplyFilters) {
      onApplyFilters({}); 
    }
    setFilterModalVisible(false);
  };
  const removeFilter = (filterKey) => {
    const newFilters = { ...activeFilters };
    delete newFilters[filterKey];
    
    if (filterKey === 'created_range') {
      delete newFilters.created_range;
    } else if (filterKey === 'updated_range') {
      delete newFilters.updated_range;
    } else if (filterKey === 'sort') {
      delete newFilters.sort_by;
      delete newFilters.sort_order;
    }
    
    setActiveFilters(newFilters);
    if (onApplyFilters) {
      onApplyFilters(newFilters);
    }
  };

  const handleAddChannel = async () => {
    try {
      if (!channelForm.name || !channelForm.client_id || !channelForm.client_secret) {
        toast.error("Пожалуйста, заполните все поля");
        return;
      }

      await apiRequests.avito.connect({
        api_key: channelForm.client_id,
        api_secret: channelForm.client_secret,
        channel_name: channelForm.name,
        grant_type: "client_credentials", 
      });

      toast.success("Канал успешно подключен через персональную авторизацию!");
      setIsAddChannelOpen(false);
      setChannelForm({
        name: "",
        client_id: "",
        client_secret: ""
      });
      
      loadChannels();
    } catch (error) {
      console.error("Ошибка подключения канала:", error);
      if (error.response?.data?.detail) {
        toast.error(`Ошибка: ${error.response.data.detail}`);
      } else {
        toast.error("Не удалось подключить канал Avito. Проверьте правильность данных.");
      }
    }
  };

  const handleOAuthConnect = async () => {
    try {
      const response = await apiRequests.avito.connect({
        channel_name: channelForm.name || "Avito OAuth",
        grant_type: "authorization_code", 
      });

      if (response.data?.authorization_url) {
        toast.success("Переходим к авторизации на Avito");
        window.open(response.data.authorization_url, '_blank');
        setIsAddChannelOpen(false);
        setChannelForm({
          name: "",
          client_id: "",
          client_secret: ""
        });
      } else {
        toast.error("Не удалось получить ссылку авторизации");
      }
    } catch (error) {
      console.error("Ошибка OAuth подключения:", error);
      if (error.response?.data?.detail) {
        toast.error(`Ошибка: ${error.response.data.detail}`);
      } else {
        toast.error("Не удалось получить ссылку для OAuth авторизации");
      }
    }
  };

  const handleEditChannel = async () => {
    if (!selectedChannel) return;
    
    try {
      await apiRequests.chats.updateChannel(selectedChannel.id, channelForm);
      toast.success("Канал успешно обновлен");
      setIsEditChannelOpen(false);
      setSelectedChannel(null);
      loadChannels();
    } catch (error) {
      console.error("Ошибка обновления канала:", error);
      toast.error(error.response?.data?.detail || "Ошибка обновления канала");
    }
  };

  const handleDeleteChannel = async (channelId) => {
    try {
      await apiRequests.chats.deleteChannel(channelId);
      toast.success("Канал успешно удален");
      loadChannels();
    } catch (error) {
      console.error("Ошибка удаления канала:", error);
      toast.error(error.response?.data?.detail || "Ошибка удаления канала");
    }
  };

  const handleEditClick = (channel) => {
    setSelectedChannel(channel);
    setChannelForm({
      name: channel.name,
      type: channel.type,
      description: channel.description || "",
      svg_icon: channel.svg_icon || "",
      tags: channel.tags || {},
      api_config_name: channel.api_config_name || "",
      is_active: channel.is_active
    });
    setIsEditChannelOpen(true);
  };

  const renderActiveFilters = () => {
    if (Object.keys(activeFilters).length === 0) return null;
    
    const filters = [];

    
    if (activeFilters.channel_id) {
      const channel = allChannels.find(c => c.id.toString() === activeFilters.channel_id);
      filters.push({
        key: 'channel_id',
        label: `Канал: ${channel?.name || activeFilters.channel_id}`
      });
    }

    
    if (activeFilters.status) {
      filters.push({
        key: 'status',
        label: `Статус: ${activeFilters.status}`
      });
    }

    
    if (activeFilters.contragent_id) {
      const contragent = contragentOptions.find(c => c.value === activeFilters.contragent_id);
      filters.push({
        key: 'contragent_id',
        label: `Контрагент: ${contragent?.label || activeFilters.contragent_id}`
      });
    }

    
    if (activeFilters.search) {
      filters.push({
        key: 'search',
        label: `Поиск: ${activeFilters.search}`
      });
    }

    
    if (activeFilters.created_range?.from || activeFilters.created_range?.to) {
      const from = activeFilters.created_range.from ? format(activeFilters.created_range.from, 'dd.MM.yyyy') : '';
      const to = activeFilters.created_range.to ? format(activeFilters.created_range.to, 'dd.MM.yyyy') : '';
      filters.push({
        key: 'created_range',
        label: `Дата создания: ${from} - ${to}`
      });
    }

    
    if (activeFilters.updated_range?.from || activeFilters.updated_range?.to) {
      const from = activeFilters.updated_range.from ? format(activeFilters.updated_range.from, 'dd.MM.yyyy') : '';
      const to = activeFilters.updated_range.to ? format(activeFilters.updated_range.to, 'dd.MM.yyyy') : '';
      filters.push({
        key: 'updated_range',
        label: `Дата обновления: ${from} - ${to}`
      });
    }

    
    const defaultSortBy = "last_message_time";
    const defaultSortOrder = "desc";
    
    if (activeFilters.sort_by !== defaultSortBy || activeFilters.sort_order !== defaultSortOrder) {
      const sortByLabels = {
        'last_message_time': 'Последнему сообщению',
        'created_at': 'Дате создания',
        'updated_at': 'Дате обновления',
        'name': 'Имени'
      };
      const sortOrderLabels = {
        'desc': 'убывание',
        'asc': 'возрастание'
      };
      filters.push({
        key: 'sort',
        label: `Сортировка: ${sortByLabels[activeFilters.sort_by]} (${sortOrderLabels[activeFilters.sort_order]})`
      });
    }

    if (filters.length === 0) return null;
    
    return (
      <div className="px-4 py-2 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 border-b">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-blue-600">Активные фильтры:</span>
          {filters.map(filter => (
            <Badge 
              key={filter.key} 
              variant="outline" 
              className="pl-2 pr-1.5 py-1 gap-1.5 hover:bg-blue-50 cursor-pointer transition-all duration-200 border-blue-200 bg-white shadow-sm hover:shadow-md hover:border-blue-300 group"
              onClick={() => removeFilter(filter.key)}
            >
              <span className="text-xs text-blue-700">{filter.label}</span>
              <X className="h-3 w-3 text-blue-500 group-hover:text-blue-700 transition-colors" />
            </Badge>
          ))}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={resetFilters}
            className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-100/50"
          >
            Сбросить все
          </Button>
        </div>
      </div>
    );
  };

  const renderChatItem = (chat) => {
    const isSelected = selectedChat?.id === chat.id;
    const isTyping = typingStatus[chat.id]?.isTyping || false;
    const typingUserType = typingStatus[chat.id]?.userType;
    const isOnline = onlineStatus[chat.id] && 
                    Object.keys(onlineStatus[chat.id]).length > 0;
    
    let lastMessageTime = formatLastMessageTime(chat.last_message_time);
    const isUnread = chat.unread_count > 0;
    
    let lastMessage = "Нет сообщений";
    let messagePreview = null;
    
    if (chat.last_message_preview && isImageMessage(chat.last_message_preview)) {
      lastMessage = "Изображение";
    } else if (chat.last_message_preview) {
      lastMessage = truncatePreview(chat.last_message_preview, 40);
    }
    
    if (isTyping) {
      if (typingUserType) {
        messagePreview = (
          <div className="flex items-center gap-1 text-blue-600">
            <div className="flex items-center gap-0.5">
              <div className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
              <div className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" style={{ animationDelay: '0.2s' }} />
              <div className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" style={{ animationDelay: '0.4s' }} />
            </div>
            <span className="text-xs">печатает...</span>
          </div>
        );
      }
    }
    
    if (!messagePreview && chat.last_message_preview) {
      lastMessage = truncatePreview(chat.last_message_preview, 40);
    }

    return (
      <div
        key={chat.id}
        onClick={() => onSelectChat(chat)}
        className={cn(
          "cursor-pointer p-3 pr-2 flex ease-out relative",
          isSelected 
            ? "bg-gradient-to-r from-blue-50/80 to-indigo-50/80 shadow-sm" 
            : "hover:bg-gray-50/80",
        )}
      >
        <div className="relative flex-shrink-0">
          <Avatar className="h-12 w-12 border-2 border-white shadow-md ring-2 ring-offset-2 ring-transparent transition-all duration-300">
            <AvatarImage 
              src={chat.contact?.avatar} 
              alt={chat.contact?.name || chat.name}
              className="object-cover"
            />
            <AvatarFallback className={cn(
              "bg-gradient-to-br text-lg font-medium",
              isSelected 
                ? "from-blue-100 to-blue-200 text-blue-700" 
                : "from-gray-100 to-gray-200 text-gray-600"
            )}>
              {chat.contact?.name?.[0]?.toUpperCase() || chat.name?.[0]?.toUpperCase() || <User className="h-6 w-6" />}
            </AvatarFallback>
          </Avatar>

          {/* Иконка канала */}
          {chat.channel_icon && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full p-0.5 shadow-sm border border-gray-200 flex items-center justify-center">
              <div 
                className="w-full h-full [&_svg]:w-full [&_svg]:h-full [&_svg]:text-gray-600"
                dangerouslySetInnerHTML={{ __html: chat.channel_icon }}
              />
            </div>
          )}
          
          {/* Индикатор онлайн статуса - ТОЛЬКО если онлайн и не текущий пользователь */}
          {isOnline && chat.contact && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-sm" />
          )}
        </div>
        
        <div className="flex-1 min-w-0 pl-2">
          <div className="flex justify-between items-start gap-2 mb-1.5">
            <div className="flex items-center gap-2">
              <p className={cn(
                "font-semibold text-sm truncate text-ellipsis transition-colors",
                isSelected 
                  ? "text-blue-700" 
                  : "text-gray-900",
                isUnread && !isSelected && "font-bold"
              )}>
                {chat.contact?.name || chat.name || "Без имени"}
              </p>
            </div>
            
            <span className={cn(
              "text-xs whitespace-nowrap transition-colors",
              isSelected 
                ? "text-blue-600" 
                : "text-gray-500",
              isTyping && "text-blue-600 font-medium"
            )}>
              {isTyping ? "сейчас" : lastMessageTime}
            </span>
          </div>
          <div className="flex justify-between items-start gap-2 mb-1.5">
            <div className="text-sm truncate transition-colors leading-tight min-h-[20px] flex items-center">
              {messagePreview || (
                <p className={cn(
                  isSelected 
                    ? "text-blue-600/80" 
                    : "text-gray-600",
                  isUnread && !isSelected && "font-medium text-gray-800"
                )}>
                  {lastMessage}
                </p>
              )}
            </div>
            {isUnread && !isTyping && (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white shadow-lg">
                {chat.unread_count > 9 ? "9+" : chat.unread_count}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const DateRangePicker = ({ value, onChange, className }) => (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal transition-all duration-200 border-gray-300 hover:border-blue-500 hover:bg-blue-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-200",
              !value && "text-gray-500",
              value && "border-blue-500 bg-blue-50"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value?.from ? (
              value.to ? (
                <>
                  {format(value.from, "dd.MM.yyyy")} - {format(value.to, "dd.MM.yyyy")}
                </>
              ) : (
                format(value.from, "dd.MM.yyyy")
              )
            ) : (
              <span className="text-gray-500">Выберите диапазон</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto p-0 shadow-xl border-gray-200" 
          align="start"
          style={{ minWidth: '580px' }}
        >
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={value?.from}
            selected={value}
            onSelect={onChange}
            numberOfMonths={2}
            locale={ru}
            className="rounded-lg border"
            classNames={{
              day: cn(
                "h-10 w-10 p-0 font-normal aria-selected:opacity-100",
                "hover:bg-gray-500 rounded-md transition-colors"
              ),
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );

  
  const filteredChannels = allChannels.filter(channel =>
    channel.name.toLowerCase().includes(channelsSearchQuery.toLowerCase()) ||
    (channel.description && channel.description.toLowerCase().includes(channelsSearchQuery.toLowerCase())) ||
    channel.type.toLowerCase().includes(channelsSearchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full border-r bg-white shadow-inner">
      {/* Header */}
      <div className="p-4 border-b bg-gradient-to-r from-white to-gray-50">
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={activeTab === "chats" ? "Поиск чатов по имени или телефону" : "Поиск каналов..."}
                value={activeTab === "chats" ? localSearch : channelsSearchQuery}
                onChange={(e) => activeTab === "chats" ? setLocalSearch(e.target.value) : setChannelsSearchQuery(e.target.value)}
                className="pl-10 bg-white border-gray-300 hover:border-blue-500 active:!border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
              />
            </div>
            {activeTab === "chats" && (
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleFilterClick}
                className="border-gray-300 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200"
              >
                <Filter className="h-4 w-4" />
              </Button>
            )}
            {activeTab === "channels" && (
              <Button
                variant="outline" 
                size="icon"
                onClick={() => setIsAddChannelOpen(true)}
                className="border-gray-300 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200"
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
          
          {/* Tab switcher buttons under search */}
          <div className="flex space-x-2">
            <Button
              variant={activeTab === "chats" ? "link" : "outline"}
              size="sm"
              className="flex-1"
              onClick={() => setActiveTab("chats")}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Чаты
            </Button>
            <Button
              variant={activeTab === "channels" ? "link" : "outline"}
              size="sm"
              className="flex-1"
              onClick={() => setActiveTab("channels")}
            >
              <Hash className="w-4 h-4 mr-2" />
              Каналы
            </Button>
          </div>
        </div>
      </div>

      {activeTab === "chats" && renderActiveFilters()}

      {/* Chat Count / Channels Count */}
      {activeTab === "chats" ? (
        <div className="px-4 py-2.5 border-b bg-gray-50/50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Все чаты</span>
            <Badge variant="outline" className="font-medium bg-white border-gray-300 text-gray-700 shadow-sm">
              {loading ? "..." : chats.length}
            </Badge>
          </div>
        </div>
      ) : (
        <div className="px-4 py-2.5 border-b bg-gray-50/50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Все каналы</span>
            <Badge variant="outline" className="font-medium bg-white border-gray-300 text-gray-700 shadow-sm">
              {loadingChannels ? "..." : allChannels.length}
            </Badge>
          </div>
        </div>
      )}

      {/* Chat List or Channels List */}
      {activeTab === "chats" ? (
        loading ? (
          <div className="flex-1 p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-start space-x-3 animate-pulse">
                <Skeleton className="h-12 w-12 rounded-full bg-gradient-to-r from-gray-200 to-gray-300" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-1/3 bg-gradient-to-r from-gray-200 to-gray-300" />
                  <Skeleton className="h-3 w-2/3 bg-gradient-to-r from-gray-200 to-gray-300" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ScrollArea className="flex-1">
            <div className="divide-y divide-gray-100">
              {chats.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                  <div className="rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 p-5 mb-5 shadow-inner">
                    <MessageSquare className="h-10 w-10 text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Чаты не найдены</h3>
                  <p className="text-sm text-gray-500 mb-6 max-w-[220px]">
                    Попробуйте изменить параметры поиска или фильтры
                  </p>
                </div>
              ) : (
                chats.map(renderChatItem)
              )}
            </div>
          </ScrollArea>
        )
      ) : (
        /* Channels List */
        loadingChannels ? (
          <div className="flex-1 p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center space-x-3 animate-pulse p-3">
                <Skeleton className="h-12 w-12 rounded-md bg-gradient-to-r from-gray-200 to-gray-300" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-1/2 bg-gradient-to-r from-gray-200 to-gray-300" />
                  <Skeleton className="h-3 w-1/3 bg-gradient-to-r from-gray-200 to-gray-300" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredChannels.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <div className="rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 p-5 mb-5 shadow-inner">
              <Hash className="h-10 w-10 text-blue-400" />
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">
              {channelsSearchQuery ? "Каналы не найдены" : "Каналы не найдены"}
            </h3>
            <p className="text-sm text-gray-500 mb-6 max-w-[220px]">
              {channelsSearchQuery 
                ? `По запросу "${channelsSearchQuery}" ничего не найдено`
                : "Создайте первый канал для начала работы"}
            </p>
            <Button onClick={() => setIsAddChannelOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Создать канал
            </Button>
          </div>
        ) : (
          <ScrollArea className="flex-1">
            <div className="divide-y divide-gray-100">
              {filteredChannels.map((channel) => (
                <div
                  key={channel.id}
                  className="p-3 border-b flex items-start gap-3 transition-all duration-300 ease-out hover:bg-gray-50/80 cursor-default"
                >
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 rounded-md bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                      {channel.svg_icon ? (
                        <div 
                          className="w-6 h-6 [&_svg]:w-full [&_svg]:h-full [&_svg]:text-blue-600"
                          dangerouslySetInnerHTML={{ __html: channel.svg_icon }}
                        />
                      ) : (
                        <Hash className="h-6 w-6 text-blue-600" />
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2 mb-1.5">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm truncate text-ellipsis text-gray-900">
                          {channel.name}
                        </p>
                        {channel.is_active ? (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            <Eye className="w-3 h-3 mr-1" />
                            Активен
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <EyeOff className="w-3 h-3 mr-1" />
                            Неактивен
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditClick(channel)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Удаление канала</AlertDialogTitle>
                              <AlertDialogDescription>
                                Вы уверены, что хотите удалить канал "{channel.name}"? 
                                Это действие нельзя отменить.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Отмена</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteChannel(channel.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Удалить
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    
                    {channel.description && (
                      <p className="text-sm text-gray-600 leading-tight">
                        {channel.description}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-3 mt-2">
                      <Badge variant="outline" className="text-xs">
                        {channel.type}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        Создан: {channel.created_at ? format(parseISO(channel.created_at), "dd.MM.yyyy") : "Нет данных"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )
      )}

      {/* Filter Modal */}
      <Dialog open={filterModalVisible} onOpenChange={setFilterModalVisible}>
        <DialogContent className="sm:max-w-[580px] bg-white p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Фильтры чатов
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Настройте параметры для фильтрации списка чатов
            </DialogDescription>
          </DialogHeader>
          <Separator className="bg-gray-200" />
          <Form {...filterForm}>
            <form onSubmit={filterForm.handleSubmit(applyFilters)} className="max-h-[70vh] flex flex-col">
              <ScrollArea className="flex-1 px-6">
                <div className="space-y-5 py-2 px-1">
                  <FormField
                    control={filterForm.control}
                    name="search"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700 font-medium">Поиск</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Имя, телефон или сообщение..." 
                            {...field} 
                            className="border-gray-300 bg-white hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all w-full justify-self-center"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={filterForm.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-700 font-medium ">Статус</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value} className="w-full">
                            <FormControl className="w-full">
                              <SelectTrigger className="w-full border-gray-300 bg-white hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all">
                                <SelectValue placeholder="Все статусы" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-white border-gray-200 shadow-lg" position="popper">
                              <SelectItem value="ACTIVE" className="hover:bg-blue-50 cursor-pointer transition-colors">Активные</SelectItem>
                              <SelectItem value="ARCHIVED" className="hover:bg-blue-50 cursor-pointer transition-colors">Архивные</SelectItem>
                              <SelectItem value="CLOSED" className="hover:bg-blue-50 cursor-pointer transition-colors">Закрытые</SelectItem>
                              <SelectItem value="PENDING" className="hover:bg-blue-50 cursor-pointer transition-colors">В ожидании</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={filterForm.control}
                      name="channel_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-700 font-medium">Канал</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="w-full border-gray-300 bg-white hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all">
                                <SelectValue placeholder="Все каналы" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-white border-gray-200 shadow-lg" position="popper">
                              {allChannels.map(channel => (
                                <SelectItem 
                                  key={channel.id} 
                                  value={channel.id.toString()}
                                  className="hover:bg-blue-50 cursor-pointer transition-colors"
                                >
                                  {channel.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <FormLabel className="text-gray-700 font-medium">Диапазон дат</FormLabel>
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={filterForm.control}
                        name="created_range"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs text-gray-600">Дата создания</FormLabel>
                            <FormControl>
                              <DateRangePicker value={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={filterForm.control}
                        name="updated_range"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs text-gray-600">Дата обновления</FormLabel>
                            <FormControl>
                              <DateRangePicker value={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={filterForm.control}
                      name="sort_by"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-700 font-medium">Сортировать по</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="w-full border-gray-300 bg-white hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all">
                                <SelectValue placeholder="Выберите поле" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-white border-gray-200 shadow-lg" position="popper">
                              <SelectItem value="last_message_time" className="hover:bg-blue-50 cursor-pointer transition-colors">Последнему сообщению</SelectItem>
                              <SelectItem value="created_at" className="hover:bg-blue-50 cursor-pointer transition-colors">Дате создания</SelectItem>
                              <SelectItem value="updated_at" className="hover:bg-blue-50 cursor-pointer transition-colors">Дате обновления</SelectItem>
                              <SelectItem value="name" className="hover:bg-blue-50 cursor-pointer transition-colors">Имени</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={filterForm.control}
                      name="sort_order"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-700 font-medium">Порядок</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="w-full border-gray-300 bg-white hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all">
                                <SelectValue placeholder="Выберите порядок" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-white border-gray-200 shadow-lg"  position="popper">
                              <SelectItem value="desc" className="hover:bg-blue-50 cursor-pointer transition-colors">По убыванию</SelectItem>
                              <SelectItem value="asc" className="hover:bg-blue-50 cursor-pointer transition-colors">По возрастанию</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </ScrollArea>
              <Separator className="bg-gray-200 mt-6" />
              <DialogFooter className="p-6 pt-6 bg-gray-50/50">
                <div className="flex justify-between w-full">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetFilters}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    Сбросить все
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setFilterModalVisible(false)}
                      className="border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:border-gray-400 transition-all"
                    >
                      Отмена
                    </Button>
                    <Button 
                      type="submit"
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all"
                    >
                      Применить фильтры
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddChannelOpen} onOpenChange={setIsAddChannelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Добавление канала</DialogTitle>
          </DialogHeader>
          
          {!showOAuthView ? (
            <>
              <div className="space-y-4 py-4">
                <div style={{ padding: "0 10px", marginTop: 0, marginBottom: 24 }}>
                  <p style={{ margin: 0, fontSize: 15, color: "#000000", textAlign: "center", lineHeight: "1.6" }}>
                    Введите ниже данные из <span style={{ color: "#1890ff" }}>Вашего личного кабинета Avito</span> для добавления аккаунта.
                    <br />
                    Мы рекомендуем использовать использовать <span style={{ color: "#419265", fontWeight: 700 }}>oAuth авторизацию</span>. 
                    Так Вам необходимо будет только предоставить доступ приложению на самом сайте <span style={{ color: "#419265", fontWeight: 700 }}>avito.ru</span>
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Название канала</Label>
                  <Input
                    id="name"
                    value={channelForm.name}
                    onChange={(e) => setChannelForm({...channelForm, name: e.target.value})}
                    placeholder="Название канала"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_id">client_id</Label>
                  <Input
                    id="client_id"
                    value={channelForm.client_id}
                    onChange={(e) => setChannelForm({...channelForm, client_id: e.target.value})}
                    placeholder="client_id"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_secret">client_secret</Label>
                  <Input
                    id="client_secret"
                    type="password"
                    value={channelForm.client_secret}
                    onChange={(e) => setChannelForm({...channelForm, client_secret: e.target.value})}
                    placeholder="client_secret"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setShowOAuthView(true)}>
                  Использовать OAuth авторизацию
                </Button>
                <Button variant="outline" onClick={handleAddChannel}>
                  Добавить канал
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div style={{ padding: "0 10px", textAlign: "center" }}>
                  <p style={{ margin: "0 0 24px 0", fontSize: 15, color: "#000000", textAlign: "center", lineHeight: "1.6" }}>
                    Используйте кнопку ниже для добавления Вашего аккаунта Avito
                  </p>
                  <div style={{ marginBottom: "30px" }}>
                    <Button 
                      onClick={handleOAuthConnect}
                      style={{ minWidth: "200px" }}
                    >
                      Подключить аккаунт
                    </Button>
                  </div>
                  <p style={{ margin: 0, fontSize: 13, color: "#666", textAlign: "center", lineHeight: "1.6" }}>
                    Мы перебросим Вас на страницу Avito, где Вам необходимо будет выбрать аккаунт и разрешить доступ приложению
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setShowOAuthView(false)} variant="outline" style={{ padding: "0 10px", height: "auto" }}>
                  Использовать персональную авторизацию
                </Button>
                <Button variant="outline" onClick={() => setIsAddChannelOpen(false)}>
                  Закрыть
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Модальное окно редактирования канала */}
      <Dialog open={isEditChannelOpen} onOpenChange={setIsEditChannelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Редактирование канала</DialogTitle>
            <DialogDescription>
              Измените информацию о канале
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Название канала *</Label>
              <Input
                id="edit-name"
                value={channelForm.name}
                onChange={(e) => setChannelForm({...channelForm, name: e.target.value})}
                placeholder="Введите название канала"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-type">Тип канала *</Label>
              <Input
                id="edit-type"
                value={channelForm.type}
                onChange={(e) => setChannelForm({...channelForm, type: e.target.value})}
                placeholder="Например: telegram, whatsapp, vk"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Описание</Label>
              <Input
                id="edit-description"
                value={channelForm.description}
                onChange={(e) => setChannelForm({...channelForm, description: e.target.value})}
                placeholder="Краткое описание канала"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-svg_icon">SVG иконка</Label>
              <Input
                id="edit-svg_icon"
                value={channelForm.svg_icon}
                onChange={(e) => setChannelForm({...channelForm, svg_icon: e.target.value})}
                placeholder='<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">...</svg>'
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-api_config_name">Название конфигурации API</Label>
              <Input
                id="edit-api_config_name"
                value={channelForm.api_config_name}
                onChange={(e) => setChannelForm({...channelForm, api_config_name: e.target.value})}
                placeholder="Название конфигурации для API"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit-is_active"
                checked={channelForm.is_active}
                onChange={(e) => setChannelForm({...channelForm, is_active: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="edit-is_active" className="cursor-pointer">
                Канал активен
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditChannelOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleEditChannel}>
              Сохранить изменения
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});